import { useEffect } from 'react';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { useLayout } from '@portal/platform/configuration';
import { PlatformProvider, useSession, type PlatformSession } from '@portal/platform/session';
import {
  Button,
  LoadingState,
  PortalBrandingContext,
  ShellLocaleProvider,
  StatusPage,
  useShellTexts,
  type PortalBranding,
} from '@portal/ui';
import { LoginLogo } from './components/brand';
import { APP_CONFIG, APP_ID } from './config';
import type { AppRouter } from './router';

/** The brand this bundle was built for, as the shell and the login page show it. */
const BRANDING: PortalBranding = {
  name: APP_CONFIG.BRAND_NAME,
  logo: <LoginLogo />,
  signUpUrl: APP_CONFIG.SIGN_UP_URL,
};

/** Shown while the session is being established, and when whoami failed for a non-auth reason. */
function SessionGate({ failed, onRetry }: { failed: boolean; onRetry: () => void }) {
  const t = useShellTexts().feedback;
  if (failed) {
    return (
      <StatusPage title={t.errorMessage} message="">
        <Button variant="primary" icon="refresh" onClick={onRetry}>
          {t.retry}
        </Button>
      </StatusPage>
    );
  }
  return <LoadingState message={t.loading} />;
}

function RoutedApp({ router }: { router: AppRouter }) {
  const session = useSession();
  const language = useLayout().data?.meta.language;
  // The router runs only once the session is known — whoami has answered and a logged-in user
  // has a node — so no guard ever sees "pending" or an unknown scope.
  const ready =
    session.status === 'anonymous' ||
    (session.status === 'authenticated' && session.scope.current !== null);

  // Guards read the session from router context; rerun them whenever it changes.
  const userId = session.identity?.userId ?? null;
  const nodeId = session.scope.current?.id ?? null;
  const viewMode = session.scope.viewMode;
  useEffect(() => {
    if (ready) {
      void router.invalidate();
    }
  }, [router, ready, userId, nodeId, viewMode]);

  return (
    <ShellLocaleProvider language={language}>
      <PortalBrandingContext value={BRANDING}>
        {ready ? (
          <RouterProvider router={router} context={{ session }} />
        ) : (
          <SessionGate
            failed={session.status === 'error'}
            onRetry={() => window.location.reload()}
          />
        )}
      </PortalBrandingContext>
    </ShellLocaleProvider>
  );
}

/** The whole app: query cache, session + configuration, router. Tests render it too. */
export function App({
  queryClient,
  platform,
  router,
}: {
  queryClient: QueryClient;
  platform: PlatformSession;
  router: AppRouter;
}) {
  return (
    <QueryClientProvider client={queryClient}>
      <PlatformProvider platform={platform} appId={APP_ID}>
        <RoutedApp router={router} />
      </PlatformProvider>
    </QueryClientProvider>
  );
}
